import React, { useEffect } from 'react';
import useBoardStore from '../store/boardStore';
import './Connector.css';

const Connector = ({ connector, stickers }) => {
  const { selectedConnector, setSelectedConnector, deleteConnector } = useBoardStore();
  const fromSticker = stickers.find(s => s.id === connector.from_id);
  const toSticker = stickers.find(s => s.id === connector.to_id);
  
  const isSelected = selectedConnector === connector.id;

  // Handle keyboard events for deletion
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isSelected && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        deleteConnector(connector.id);
      }
    };

    if (isSelected) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isSelected, connector.id, deleteConnector]);

  if (!fromSticker || !toSticker) {
    return null;
  }

  const handleClick = (event) => {
    event.stopPropagation();
    setSelectedConnector(isSelected ? null : connector.id);
  };
  
  const fromX = fromSticker.position.x;
  const fromY = fromSticker.position.y;
  const toX = toSticker.position.x;
  const toY = toSticker.position.y;
  
  // Calculate the connection points on the edges of the stickers
  const dx = toX - fromX;
  const dy = toY - fromY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  if (distance === 0) return null;
  
  const unitX = dx / distance;
  const unitY = dy / distance;
  
  // Calculate offset from center to edge based on sticker size
  const fromWidth = fromSticker.size?.width || 120;
  const fromHeight = fromSticker.size?.height || 80;
  const toWidth = toSticker.size?.width || 120;
  const toHeight = toSticker.size?.height || 80;
  
  const fromRadius = Math.max(fromWidth, fromHeight) / 2;
  const toRadius = Math.max(toWidth, toHeight) / 2;
  
  const startX = fromX + unitX * fromRadius;
  const startY = fromY + unitY * fromRadius;
  const endX = toX - unitX * toRadius;
  const endY = toY - unitY * toRadius;
  
  // Calculate midpoint for delete button
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g className={`connector ${isSelected ? 'selected' : ''}`}>
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={isSelected ? "#007bff" : "#666"}
        strokeWidth={isSelected ? "3" : "2"}
        markerEnd="url(#arrowhead)"
        className="connector-line"
        onClick={handleClick}
      />
      
      {/* Invisible thicker line for easier clicking */}
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke="transparent"
        strokeWidth="10"
        className="connector-clickable"
        onClick={handleClick}
      />
      
      {/* Delete button when selected */}
      {isSelected && (
        <g>
          <circle
            cx={midX}
            cy={midY}
            r="12"
            fill="white"
            stroke="#dc3545"
            strokeWidth="2"
            className="connector-delete-bg"
          />
          <text
            x={midX}
            y={midY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fill="#dc3545"
            className="connector-delete-icon"
            onClick={(e) => {
              e.stopPropagation();
              deleteConnector(connector.id);
            }}
          >
            ✕
          </text>
        </g>
      )}
    </g>
  );
};

export default Connector;
